<template>
  <div class="gift-givers">
    <div class="gift-icon">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M20 12v10H4V12M22 7H2v5h20V7zM12 22V7M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
      </svg>
    </div>

    <div class="scroll-container" v-if="uniqueGivers.length > 5">
      <button
        class="scroll-button left" 
        @click="scroll('left')"
        v-if="!isMobile && canScrollLeft">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M15 18l-6-6 6-6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div class="scroll-wrapper">
        <div class="givers-grid" ref="giversGrid">
          <div v-for="giver in uniqueGivers"
               :key="giver.uuid"
               class="giver-avatar small">
            <img :src="giver.avatar" :alt="giver.name || 'Giver'" />
            <span class="total-gift-count">{{ giver.totalGiftCount }}</span>
          </div>
        </div>
      </div>

      <button 
        class="scroll-button right" 
        @click="scroll('right')"
        v-if="!isMobile && canScrollRight">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M9 18l6-6-6-6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <div class="givers-grid" v-else>
      <div v-for="giver in uniqueGivers" 
           :key="giver.uuid" 
           class="giver-avatar">
        <img :src="giver.avatar" :alt="giver.name || 'Giver'" />
        <span class="total-gift-count">{{ giver.totalGiftCount }}</span>
      </div>
    </div>
  </div>
</template>

<script>
const FALLBACK_AVATAR_GIFT = require('@/assets/images/auth/female-avatar.png'); // Fallback if needed

export default {
  name: 'GiftGivers',
  props: {
    givers: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  computed: {
    uniqueGivers() {
      const aggregation = new Map(); // Use Map for easier aggregation

      this.givers.forEach(giver => {
        const uuid = giver.userUuid;
        const count = giver.count || 0; // Ensure count is a number

        if (!uuid) return; // Skip if uuid is missing

        if (aggregation.has(uuid)) {
          // User exists, add to their count
          const existing = aggregation.get(uuid);
          existing.totalGiftCount += count;
        } else {
          // New user, add to map
          aggregation.set(uuid, {
            uuid: uuid,
            name: giver.userName,
            avatar: giver.userImage || FALLBACK_AVATAR_GIFT,
            totalGiftCount: count // Initialize count
          });
        }
      });

      // Convert Map values to an array
      return Array.from(aggregation.values());
    }
  },

  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
      isMobile: false
    }
  },

  mounted() {
    this.checkScroll();
    this.checkDevice();
    if (this.$refs.giversGrid) {
      this.$refs.giversGrid.addEventListener('scroll', this.checkScroll);
      window.addEventListener('resize', this.handleResize);
    }
  },

  beforeDestroy() {
    if (this.$refs.giversGrid) {
      this.$refs.giversGrid.removeEventListener('scroll', this.checkScroll);
      window.removeEventListener('resize', this.handleResize);
    }
  },

  methods: {
    scroll(direction) {
      if (!this.$refs.giversGrid) return;
      
      const scrollAmount = 200;
      const currentScroll = this.$refs.giversGrid.scrollLeft;
      
      this.$refs.giversGrid.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    },

    checkScroll() {
      if (!this.$refs.giversGrid) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.$refs.giversGrid;
      this.canScrollLeft = scrollLeft > 0;
      this.canScrollRight = scrollLeft < (scrollWidth - clientWidth - 1);
    },

    handleResize() {
      this.checkScroll();
      this.checkDevice();
    },

    checkDevice() {
      this.isMobile = window.innerWidth <= 768;
    }
  }
}
</script>

<style lang="scss" scoped>
.gift-givers {
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  overflow: hidden;

  .gift-icon {
    width: 40px;
    height: 40px;
    background: #FF9500;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 20px;
      height: 20px;
      stroke: white;
    }
  }

  .scroll-container {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
    margin: 0 auto;

    @media (max-width: 768px) {
      max-width: 400px;
    }

    @media (min-width: 769px) {
      max-width: 600px;
    }

    .scroll-button {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: rgba(166, 68, 251, 0.1);
      border: 1px solid rgba(166, 68, 251, 0.2);
      color: #A644FB;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      flex-shrink: 0;
      opacity: 0.6;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(166, 68, 251, 0.2);
        opacity: 1;
      }

      svg {
        width: 16px;
        height: 16px;
        stroke: currentColor;
      }
    }

    .scroll-wrapper {
      flex: 1;
      overflow: hidden;
      position: relative;
    }
  }

  .givers-grid {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    width: 100%;
    
    @media (max-width: 768px) {
      padding: 4px;
      cursor: grab;
      
      &:active {
        cursor: grabbing;
      }
    }

    @media (min-width: 769px) {
      padding: 4px 0;
    }
    
    &::-webkit-scrollbar {
      display: none;
    }

    .giver-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      position: relative;
      flex-shrink: 0;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(0, 0, 0, 0.2);
      transition: all 0.2s ease;

      img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }

      .total-gift-count {
        position: absolute;
        bottom: -2px;
        left: -2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: linear-gradient(135deg, #E91E63, #A644FB);
        color: white;
        font-size: 9px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        border: 1px solid rgba(0, 0, 0, 0.3);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
      }
      
      &.small {
        width: 32px;
        height: 32px;

        .total-gift-count {
          width: 14px;
          height: 14px;
          font-size: 8px;
          bottom: -1px;
          right: -1px;
        }
      }
    }
  }
}
</style> 